import { cn } from '@/shared/utils/cn';
import { Visual } from '@/modules/Visual/Index';
import { useState } from 'react';
import { faStar } from '@fortawesome/pro-regular-svg-icons';

interface RatingProps {
  value?: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  readonly?: boolean;
  onChange?: (value: number) => void;
  className?: string;
}

/**
 * Rating - Componente de calificación con estrellas
 *
 * Componente que muestra un sistema de calificación con estrellas.
 * Soporta modo lectura y edición, hover interactivo y diferentes tamaños.
 *
 * @example
 * ```tsx
 * // Rating de solo lectura
 * <Rating value={4} max={5} readonly />
 *
 * // Rating editable
 * <Rating
 *   value={rating}
 *   max={5}
 *   onChange={(value) => setRating(value)}
 * />
 *
 * // Rating pequeño
 * <Rating value={3} size="sm" />
 * ```
 */
export const Rating = ({
  value = 0,
  max = 5,
  size = 'md',
  readonly = true,
  onChange,
  className,
}: RatingProps) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const sizeClasses = {
    sm: 'ui-h-4 ui-w-4',
    md: 'ui-h-5 ui-w-5',
    lg: 'ui-h-6 ui-w-6',
  };

  const handleClick = (rating: number) => {
    if (!readonly) {
      onChange?.(rating);
    }
  };

  const baseClasses = cn('ui-imj', 'ui-flex ui-items-center ui-gap-1', className);

  return (
    <div className={baseClasses}>
      {Array.from({ length: max }, (_, index) => {
        const rating = index + 1;
        const isFilled = (hoverValue ?? value) >= rating;

        return (
          <button
            key={index}
            type="button"
            onClick={() => handleClick(rating)}
            onMouseEnter={() => !readonly && setHoverValue(rating)}
            onMouseLeave={() => !readonly && setHoverValue(null)}
            disabled={readonly}
            className={cn(
              'ui-transition-colors ui-duration-150',
              !readonly && 'ui-cursor-pointer hover:ui-scale-110',
              readonly && 'ui-cursor-default',
            )}
          >
            <Visual
              slot={isFilled ? faStar : faStar}
              className={cn(
                sizeClasses[size],
                isFilled ? 'ui-text-yellow-400' : 'ui-text-neutral-300',
              )}
            />
          </button>
        );
      })}
    </div>
  );
};
